<template>
  <div class="sh-page">
    <div class="sh-page__glow sh-page__glow--one" aria-hidden="true"></div>
    <div class="sh-page__glow sh-page__glow--two" aria-hidden="true"></div>

    <div class="sh-card sh-card--wide">
      <div class="sh-card__head">
        <LogoTile :branding="branding" :alt="tenantName" :size="44" />
        <h1 class="sh-card__title">Choose an organisation</h1>
        <p class="sh-card__subtitle">You have access to {{ tenantName }} through more than one organisation.</p>
      </div>

      <p v-if="error" class="sh-card__error">
        <v-icon size="15">error_outline</v-icon>
        <span>{{ error }}</span>
      </p>

      <!-- Rows are LEFT-aligned. The card centres its heading; the old version
           let that `text-align: center` cascade into the list, so every
           organisation name floated in the middle of the row while its logo
           stayed pinned to the left edge. -->
      <div class="sh-rows">
        <button
          v-for="org in session.accessibleOrgs"
          :key="org.id"
          type="button"
          class="sh-row"
          :disabled="selecting"
          @click="choose(org)"
        >
          <LogoTile :src="org.logo || org.icon || null" :alt="org.name" :size="26" />
          <span class="sh-row__label">{{ org.name }}</span>
          <v-icon size="16" class="orgpicker__chev">chevron_right</v-icon>
        </button>
      </div>

      <div class="orgpicker__foot">
        <v-btn class="orgpicker__signout" variant="text" size="small" :disabled="selecting" @click="session.signOut()"
          >Sign out</v-btn
        >
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import { useSessionStore } from "@/stores/session";
import LogoTile from "@/shell/LogoTile.vue";
import type { UserOrganisation } from "@/tenant/types";

export default defineComponent({
  name: "OrgPicker",
  components: { LogoTile },
  setup() {
    const session = useSessionStore();
    const selecting = ref(false);
    const error = ref<string | null>(null);

    const tenantName = computed(() => session.tenant?.name || "Metaship");
    const branding = computed(() => session.tenant?.branding ?? null);

    async function choose(org: UserOrganisation): Promise<void> {
      selecting.value = true;
      error.value = null;
      try {
        await session.selectOrganisation(org);
      } catch (e) {
        error.value = e instanceof Error ? e.message : "Could not switch organisation — please try again.";
      } finally {
        selecting.value = false;
      }
    }

    return { session, tenantName, branding, selecting, error, choose };
  },
});
</script>

<style scoped>
/* `.sh-page` / `.sh-card` / `.sh-row` live in src/styles/standalone.css. */
.orgpicker__chev {
  flex: 0 0 auto;
  color: var(--sh-text-45) !important;
}
/* A secondary escape hatch, not a call to action — it must not out-shout the
   organisation rows above it. */
.orgpicker__signout {
  font-size: 12px;
  color: var(--sh-text-60);
}
.orgpicker__foot {
  display: flex;
  justify-content: center;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid var(--sh-hairline);
}
</style>
